import type { Permission } from '@elsa/contracts';
import { navigate, Page } from '@elsa/ui';
import { useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useEffect, useState } from 'react';
import { OrgProvider, useAuthSession, useOrg, useOrgs } from '../lib/session.tsx';
import { supabase } from '../lib/supabase.ts';
import { AccountPage } from './Account.tsx';
import { AcquisitionPage } from './Acquisition.tsx';
import { AlertReportPage } from './AlertReport.tsx';
import { AlertsPage } from './Alerts.tsx';
import { AppLayout, allowed, NAV } from './AppLayout.tsx';
import { BillingPage } from './Billing.tsx';
import { EntityPage } from './Entity.tsx';
import { HomePage } from './Home.tsx';
import { PageLoader } from './loading.tsx';
import { ManagementPage } from './Management.tsx';
import { NotificationsPage } from './Notifications.tsx';
import { OnboardingPage } from './Onboarding.tsx';
import { CpvPage, PricesPage } from './Prices.tsx';
import { RolesPage } from './Roles.tsx';
import { SalesPage } from './Sales.tsx';
import { SearchPage } from './Search.tsx';
import { SettingsPage } from './Settings.tsx';
import { TeamPage } from './Team.tsx';
import { TerritoryPage } from './Territory.tsx';

// Ce permisiune cere o ruta: intrarea din meniu cu cel mai lung prefix, plus fisele.
function needFor(path: string): Permission[] | undefined {
  if (/^\/app\/(autoritati|furnizori)\//.test(path)) return ['data.read'];
  return NAV.filter((n) => n.to !== '/app' && (path === n.to || path.startsWith(`${n.to}/`))).sort(
    (a, b) => b.to.length - a.to.length,
  )[0]?.need;
}

/** Pasii ecranului de pornire, in ordinea in care se intampla. */
const BOOT = ['Verificăm sesiunea', 'Căutăm firmele contului', 'Pregătim tabloul de bord'];

/** Spune ecranului de pornire cand au venit drepturile firmei (sunt in OrgProvider). */
function BootReady({ onReady }: { onReady: (v: boolean) => void }) {
  const { entitlementsSettled } = useOrg();
  useEffect(() => {
    if (entitlementsSettled) onReady(true);
  }, [entitlementsSettled, onReady]);
  return null;
}

// Poarta aplicatiei: sesiune -> firma (onboarding daca nu are) -> paginile. Ecranul de pornire
// acopera totul pana vin firmele si drepturile firmei curente; aplicatia se randeaza dedesubt.
export function AppShell({ path }: { path: string }) {
  const session = useAuthSession();
  const orgs = useOrgs(session?.user.id);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (session === null) {
      navigate(`/autentificare?next=${encodeURIComponent(location.pathname + location.search)}`);
    }
  }, [session]);
  const list = orgs.data ?? [];
  const onboarding = !list.length || path === '/app/firma-noua';
  const loaded = Boolean(session) && !orgs.isLoading;
  let body: ReactNode = null;
  if (session && loaded) {
    if (orgs.error) {
      body = (
        <div className="p-6 text-sm text-semnal-700">
          Nu pot citi firmele contului: {(orgs.error as Error).message}
        </div>
      );
    } else if (onboarding) {
      body = <OnboardingPage first={!list.length} email={session.user.email ?? ''} />;
    } else {
      body = (
        <OrgProvider session={session} orgs={list}>
          <BootReady onReady={setReady} />
          <AppLayout path={path}>
            <Routes path={path} />
          </AppLayout>
        </OrgProvider>
      );
    }
  }
  // Ecranul sta mereu pe aceeasi pozitie in arbore, ca sa nu se reia de la zero intre pasi.
  return (
    <>
      {body}
      <PageLoader
        steps={BOOT}
        step={!session ? 0 : orgs.isLoading ? 1 : 2}
        done={loaded && (Boolean(orgs.error) || onboarding || ready)}
      />
    </>
  );
}

function useNotificationsRealtime() {
  const { session } = useOrg();
  const qc = useQueryClient();
  useEffect(() => {
    const ch = supabase
      .channel('elsa-notifications')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'app',
          table: 'notifications',
          filter: `user_id=eq.${session.user.id}`,
        },
        () => void qc.invalidateQueries({ queryKey: ['notifications'] }),
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(ch);
    };
  }, [session.user.id, qc]);
}

function Routes({ path }: { path: string }) {
  useNotificationsRealtime();
  const { can } = useOrg();
  if (!allowed(can, needFor(path))) {
    return (
      <Page title="Nu ai acces">
        <p className="text-sm text-gri-700">
          Rolul tău din firmă nu include această pagină. Cere administratorului firmei, dacă ai
          nevoie de ea.
        </p>
      </Page>
    );
  }
  const m = (re: RegExp) => path.match(re)?.[1];
  const acq = m(/^\/app\/achizitii\/(\d+)$/);
  if (acq) return <AcquisitionPage id={Number(acq)} />;
  const ca = m(/^\/app\/autoritati\/(\d+)$/);
  if (ca) return <EntityPage kind="authority" id={Number(ca)} />;
  const su = m(/^\/app\/furnizori\/(\d+)$/);
  if (su) return <EntityPage kind="supplier" id={Number(su)} />;
  const report = m(/^\/app\/notificari\/(\d+)$/);
  if (report) return <AlertReportPage id={Number(report)} />;
  const cpv = m(/^\/app\/preturi\/([0-9]{8}-[0-9])$/);
  if (cpv) return <CpvPage code={cpv} />;
  switch (path) {
    case '/app':
      return <HomePage />;
    case '/app/achizitii':
      return <SearchPage kind="acq" />;
    case '/app/produse':
      return <SearchPage kind="item" />;
    case '/app/preturi':
      return <PricesPage />;
    case '/app/zona':
      return <TerritoryPage />;
    case '/app/vanzari':
      return <SalesPage />;
    case '/app/management':
      return <ManagementPage />;
    case '/app/alerte':
      return <AlertsPage />;
    case '/app/notificari':
      return <NotificationsPage />;
    case '/app/echipa':
      return <TeamPage />;
    case '/app/roluri':
      return <RolesPage />;
    case '/app/setari':
      return <SettingsPage />;
    case '/app/abonament':
      return <BillingPage />;
    case '/app/cont':
      return <AccountPage />;
    default:
      return (
        <Page title="Pagina nu există">
          <p className="text-sm text-gri-700">{path}</p>
        </Page>
      );
  }
}
